import React, { useEffect, useState } from "react";
import { api, API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Search, Download, Trash2, X, Phone, Mail, MapPin, Clock, User, Tag, FileText,
  Loader2, StickyNote, MessageSquare, ExternalLink, Globe, Users, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = {
  new:       { label: "Nouveau",   color: "bg-[color:var(--brand-sky)]/15 text-[color:var(--brand-sky)] border-[color:var(--brand-sky)]/30" },
  contacted: { label: "Contacté",  color: "bg-amber-100 text-amber-700 border-amber-200" },
  won:       { label: "Gagné",     color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  lost:      { label: "Perdu",     color: "bg-red-100 text-red-700 border-red-200" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function LeadsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (source) params.source = source;
      if (q) params.q = q;
      const { data } = await api.get("/leads", { params });
      setItems(data);
    } catch { toast.error("Impossible de charger les leads"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, source]);

  const counts = items.reduce((a, l) => { a[l.status || "new"] = (a[l.status || "new"] || 0) + 1; return a; }, {});
  const sources = Array.from(new Set(items.map((l) => l.source || "direct"))).filter(Boolean);

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (source) params.set("source", source);
    const token = localStorage.getItem("kap_token");
    fetch(`${API}/leads/export.csv?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url;
        a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("Export CSV téléchargé");
      })
      .catch(() => toast.error("Export impossible"));
  };

  const exportProspectsCsv = () => {
    const token = localStorage.getItem("kap_token");
    fetch(`${API}/prospects.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const count = r.headers.get("X-Prospect-Count") || "?";
        return r.blob().then((blob) => ({ blob, count }));
      })
      .then(({ blob, count }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url;
        a.download = `prospects-kap-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success(`Fichier prospects prêt — ${count} contact(s) unique(s)`);
      })
      .catch(() => toast.error("Export prospects impossible"));
  };

  const resetAll = async () => {
    if (!window.confirm(`⚠️ RESET LEADS\n\nVous êtes sur le point de SUPPRIMER DÉFINITIVEMENT tous les leads (${items.length}).\n\nCette action est IRRÉVERSIBLE. Continuer ?`)) return;
    const phrase = window.prompt('Pour confirmer, tapez exactement :  SUPPRIMER');
    if (phrase !== "SUPPRIMER") { toast.info("Reset annulé"); return; }
    try {
      const { data } = await api.delete("/leads/reset", { params: { confirm: "DELETE" } });
      toast.success(`${data.deleted} lead(s) supprimé(s)`);
      setItems([]);
      setDetail(null);
    } catch {
      toast.error("Reset impossible");
    }
  };

  const setLeadStatus = async (id, newStatus) => {
    await api.put(`/leads/${id}`, { status: newStatus });
    toast.success("Statut mis à jour");
    setItems((list) => list.map((l) => l.id === id ? { ...l, status: newStatus } : l));
    if (detail?.id === id) setDetail({ ...detail, status: newStatus });
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce lead ? Cette action est irréversible.")) return;
    await api.delete(`/leads/${id}`);
    setItems((list) => list.filter((l) => l.id !== id));
    if (detail?.id === id) setDetail(null);
    toast.success("Supprimé");
  };

  const filteredBySearch = items.filter((l) =>
    !q || [l.name, l.email, l.phone, l.message, l.service, l.zone].some((x) => (x || "").toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="p-6 md:p-10" data-testid="admin-leads-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-2">Pipeline commercial</div>
          <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Leads &amp; demandes</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Suivi complet : détail, notes internes, sources, export CSV.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={exportCsv} data-testid="leads-export-csv">
            <Download className="w-4 h-4 mr-1.5" />Export leads
          </Button>
          <Button variant="outline" onClick={exportProspectsCsv} className="border-[color:var(--brand-sky)] text-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/10" data-testid="leads-export-prospects">
            <Users className="w-4 h-4 mr-1.5" />Export prospects (pub)
          </Button>
          <Button variant="outline" onClick={resetAll} disabled={items.length === 0} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="leads-reset">
            <AlertTriangle className="w-4 h-4 mr-1.5" />Reset leads
          </Button>
        </div>
      </header>

      {/* Status tabs */}
      <div className="flex items-center gap-1 bg-[hsl(var(--muted))] rounded-full p-1 mb-4 w-max flex-wrap" data-testid="leads-status-tabs">
        <button onClick={() => setStatus("")} className={`px-3.5 h-9 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${!status ? "bg-[color:var(--brand-navy)] text-white shadow" : "text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"}`}>
          Tous <span className="ml-1 opacity-60 tabular-nums">{items.length}</span>
        </button>
        {Object.entries(STATUSES).map(([k, v]) => (
          <button key={k} onClick={() => setStatus(k)} className={`px-3.5 h-9 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${status === k ? "bg-[color:var(--brand-navy)] text-white shadow" : "text-[hsl(var(--muted-foreground))] hover:text-[color:var(--brand-navy)]"}`} data-testid={`leads-tab-${k}`}>
            {v.label} <span className="ml-1 opacity-60 tabular-nums">{counts[k] || 0}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <Card className="p-3 border mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Rechercher nom, téléphone, email, message…" className="pl-9 h-9" data-testid="leads-search" />
        </div>
        <select value={source} onChange={(e) => setSource(e.target.value)} className="h-9 px-3 rounded-md border border-[hsl(var(--border))] text-xs font-semibold" data-testid="leads-source-filter">
          <option value="">Toutes sources</option>
          {sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Card>

      {loading ? (
        <Card className="p-20 border flex items-center justify-center gap-2 text-sm text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</Card>
      ) : filteredBySearch.length === 0 ? (
        <Card className="p-16 border text-center">
          <MessageSquare className="w-8 h-8 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
          <div className="font-display font-bold text-[color:var(--brand-navy)]">Aucun lead</div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Les demandes de devis/rappel arriveront ici en temps réel.</p>
        </Card>
      ) : (
        <Card className="border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))]/50 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Contact</th>
                <th className="text-left p-3">Projet</th>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Notes</th>
                <th className="text-left p-3">Reçu</th>
                <th className="text-left p-3">Statut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredBySearch.map((l) => (
                <tr key={l.id} className="border-t hover:bg-[hsl(var(--muted))]/20 cursor-pointer" onClick={() => setDetail(l)} data-testid={`lead-row-${l.id}`}>
                  <td className="p-3">
                    <div className="font-semibold text-[color:var(--brand-navy)]">{l.name}</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-2 mt-0.5">
                      <span className="font-mono">{l.phone}</span>
                      {l.email && <span className="truncate max-w-[160px]">{l.email}</span>}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="text-xs">{l.service || <span className="text-[hsl(var(--muted-foreground))]">—</span>}</div>
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))] flex items-center gap-1 mt-0.5">
                      {l.zone && <><MapPin className="w-2.5 h-2.5" />{l.zone}</>}
                      <span className="uppercase font-bold ml-1">· {l.type}</span>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">{l.source || "direct"}</span>
                    {l.utm_campaign && <div className="text-[10px] font-mono text-[hsl(var(--muted-foreground))] mt-0.5 truncate max-w-[120px]">{l.utm_campaign}</div>}
                  </td>
                  <td className="p-3">
                    {(l.notes?.length || 0) > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-[color:var(--brand-sky)] font-semibold"><StickyNote className="w-3 h-3" />{l.notes.length}</span>
                    ) : <span className="text-[hsl(var(--muted-foreground))] text-[11px]">—</span>}
                  </td>
                  <td className="p-3 text-[11px] font-mono whitespace-nowrap">{formatDate(l.created_at)}</td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={l.status || "new"}
                      onChange={(e) => setLeadStatus(l.id, e.target.value)}
                      className={`text-[11px] font-bold uppercase tracking-wider border rounded-full px-2.5 py-1 ${STATUSES[l.status || "new"].color}`}
                      data-testid={`lead-status-${l.id}`}
                    >
                      {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-right">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setDetail(l); }}><ExternalLink className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {detail && (
        <LeadDetailDrawer
          lead={detail}
          onClose={() => setDetail(null)}
          onStatusChange={(s) => setLeadStatus(detail.id, s)}
          onRefresh={(updated) => { setDetail(updated); setItems((l) => l.map((x) => x.id === updated.id ? updated : x)); }}
          onDelete={() => del(detail.id)}
        />
      )}
    </div>
  );
}

function LeadDetailDrawer({ lead, onClose, onStatusChange, onRefresh, onDelete }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/notes`, { text: note });
      setNote("");
      toast.success("Note ajoutée");
      onRefresh(data);
    } catch { toast.error("Impossible d'ajouter la note"); }
    finally { setSaving(false); }
  };

  const deleteNote = async (nid) => {
    try {
      await api.delete(`/leads/${lead.id}/notes/${nid}`);
      const updated = { ...lead, notes: (lead.notes || []).filter((n) => n.id !== nid) };
      onRefresh(updated);
      toast.success("Note supprimée");
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="lead-detail-drawer">
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 bg-gradient-to-br from-[color:var(--brand-navy)] to-[#0f223c] text-white">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--brand-sky)] font-bold mb-2">Lead · {lead.type}</div>
              <h2 className="font-display text-2xl font-black">{lead.name}</h2>
              <div className="text-sm text-white/70 flex items-center gap-3 mt-2">
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{formatDate(lead.created_at)}</span>
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="lead-drawer-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Quick actions */}
          <div className="flex flex-wrap gap-2 mt-4">
            {lead.phone && (
              <a href={`tel:${lead.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-[color:var(--brand-sky)] hover:bg-[color:var(--brand-sky)]/90 text-white text-xs font-bold transition" data-testid="lead-call">
                <Phone className="w-3.5 h-3.5" />{lead.phone}
              </a>
            )}
            {lead.email && (
              <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition">
                <Mail className="w-3.5 h-3.5" />{lead.email}
              </a>
            )}
          </div>
        </div>

        <div className="flex-1 p-6 space-y-5">
          {/* Status */}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[hsl(var(--muted-foreground))]">Statut</Label>
            <div className="grid grid-cols-4 gap-1.5 mt-1.5">
              {Object.entries(STATUSES).map(([k, v]) => (
                <button key={k} onClick={() => onStatusChange(k)} className={`h-9 rounded-lg text-[11px] font-bold uppercase tracking-wider border transition ${(lead.status || "new") === k ? "bg-[color:var(--brand-navy)] text-white border-[color:var(--brand-navy)]" : "bg-white hover:bg-[hsl(var(--muted))] border-[hsl(var(--border))]"}`} data-testid={`lead-status-btn-${k}`}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-3">
            {lead.service && <InfoRow icon={Tag} label="Service" value={lead.service} />}
            {lead.zone && <InfoRow icon={MapPin} label="Zone" value={lead.zone} />}
            <InfoRow icon={User} label="Type" value={lead.type} />
            <InfoRow icon={Globe} label="Source" value={lead.source || "direct"} />
            {lead.utm_campaign && <InfoRow icon={Tag} label="Campagne" value={lead.utm_campaign} />}
            {lead.page && <InfoRow icon={FileText} label="Page d'origine" value={lead.page} mono />}
            {lead.ip && <InfoRow icon={Globe} label="IP" value={lead.ip} mono />}
          </div>

          {/* Message */}
          {lead.message && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[hsl(var(--muted-foreground))]">Message client</Label>
              <div className="mt-1.5 p-3 rounded-xl bg-[hsl(var(--muted))]/50 border text-sm whitespace-pre-wrap">{lead.message}</div>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[hsl(var(--muted-foreground))] flex items-center gap-1">
              <StickyNote className="w-3 h-3" />Notes internes ({(lead.notes || []).length})
            </Label>
            <div className="mt-1.5 space-y-2 mb-3">
              {(lead.notes || []).map((n) => (
                <div key={n.id} className="p-3 rounded-xl border border-[hsl(var(--border))] bg-amber-50/50" data-testid={`lead-note-${n.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="text-[10px] font-mono text-[hsl(var(--muted-foreground))]">{n.author} · {formatDate(n.ts)}</div>
                    <button onClick={() => deleteNote(n.id)} className="text-[hsl(var(--muted-foreground))] hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{n.text}</div>
                </div>
              ))}
              {(!lead.notes || !lead.notes.length) && <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Aucune note. Ajoutez-en une pour garder le suivi commercial.</div>}
            </div>
            <div className="flex gap-2">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex: Rappelé à 14h, RDV fixé le 12 pour visite…" className="text-sm" data-testid="lead-note-input" />
              <Button onClick={addNote} disabled={saving || !note.trim()} className="bg-[color:var(--brand-navy)] font-bold self-stretch" data-testid="lead-note-submit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}
              </Button>
            </div>
          </div>
        </div>

        <div className="p-4 border-t flex items-center justify-between sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onDelete} className="text-red-600 hover:bg-red-50 border-red-200" data-testid="lead-delete-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" />Supprimer
          </Button>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, mono = false }) {
  return (
    <div className="p-3 rounded-xl border border-[hsl(var(--border))] bg-white">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold mb-1">
        <Icon className="w-3 h-3" />{label}
      </div>
      <div className={`text-sm font-semibold text-[color:var(--brand-navy)] break-all ${mono ? "font-mono text-xs" : ""}`}>{value}</div>
    </div>
  );
}
